import { CalendarBlank, Clock, MoonStars, CaretRight, WarningCircle, ShieldCheck } from "@phosphor-icons/react";
import { RUN_STATUS, categoryLabel, fmtDay, fmtTime, runState, shiftLabel } from "./shared";

export function DayBanner({ state, historyDate }) {
  if (!state) return null;
  const tz = state.timezone;
  return (
    <div className="space-y-2">
    <div data-testid="operating-day-banner" className="bg-slate-900 text-white rounded-2xl px-5 sm:px-7 py-5 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-8">
      <div className="shrink-0">
        <p className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-400">{historyDate ? "Historic record" : "Active operating day"}</p>
        <p className="font-heading text-xl sm:text-2xl font-bold mt-1 whitespace-nowrap" data-testid="operating-date">{fmtDay(historyDate || state.operating_date)}</p>
      </div>
      <div className="flex items-center gap-6 text-sm flex-wrap">
        <span className="flex items-center gap-1.5 text-slate-300"><Clock size={16} /> <span className="font-bold text-white" data-testid="venue-local-time">{state.local_time}</span> {tz}</span>
        <span className="flex items-center gap-1.5 text-slate-300"><CalendarBlank size={16} /> Current shift <span className="font-bold text-white">{shiftLabel(state.shifts, state.current_shift)}</span></span>
        {state.opened_by && !historyDate && <span className="text-slate-400 text-xs">Opened by {state.opened_by} · {fmtTime(state.opened_at, tz)}</span>}
      </div>
      {state.past_midnight && !historyDate && (
        <p data-testid="past-midnight-note" className="sm:ml-auto text-xs font-bold text-amber-300 bg-amber-400/10 border border-amber-400/30 rounded-lg px-3 py-2 flex items-center gap-1.5">
          <MoonStars size={16} weight="fill" /> Past midnight — anchored to {fmtDay(state.operating_date)} until shift close
        </p>
      )}
    </div>
    {state.stale && !historyDate && (
      <p data-testid="stale-day-note" className="text-sm font-bold text-red-700 bg-red-50 border border-red-200 rounded-xl px-4 py-3 flex items-center gap-2">
        <WarningCircle size={18} weight="fill" /> It is already {fmtDay(state.local_date)} at this venue but {fmtDay(state.operating_date)} is still open — close the shift to start today's routines. (Auto safety close after 36h.)
      </p>
    )}
    </div>
  );
}

export function ShiftTabs({ shifts, runs, value, onChange, current }) {
  return (
    <div className="flex gap-2 overflow-x-auto pb-1" data-testid="shift-tabs">
      {shifts.map((s) => {
        const mine = runs.filter((r) => r.shift === s.key);
        const done = mine.filter((r) => r.status !== "open").length;
        const on = value === s.key;
        return (
          <button key={s.key} data-testid={`shift-tab-${s.key}`} onClick={() => onChange(s.key)}
            className={`h-12 px-5 rounded-xl border text-sm font-bold flex items-center gap-2 shrink-0 tap-press ${on ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-900"}`}>
            {s.label}
            <span className={`text-[11px] font-bold px-1.5 py-0.5 rounded ${on ? "bg-white/15 text-white" : "bg-slate-100 text-slate-500"}`}>{done}/{mine.length}</span>
            {current === s.key && <span className={`h-1.5 w-1.5 rounded-full ${on ? "bg-emerald-400" : "bg-emerald-500"}`} title="Current shift" />}
          </button>
        );
      })}
    </div>
  );
}

export function RunCard({ run, config, onOpen }) {
  const key = runState(run);
  const st = RUN_STATUS[key];
  const pct = run.progress.total ? Math.round((run.progress.done / run.progress.total) * 100) : 0;
  const bar = run.status === "verified" ? "bg-emerald-500" : run.progress.failed ? "bg-red-500" : "bg-slate-900";
  return (
    <button data-testid={`run-card-${run.id}`} onClick={() => onOpen(run)}
      className="w-full text-left bg-white border border-slate-200 rounded-2xl p-5 sm:p-6 hover:border-slate-900 tap-press">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[11px] uppercase tracking-[0.15em] font-bold text-slate-400">{categoryLabel(config, run.category)}</p>
          <h3 className="font-heading font-bold text-lg mt-0.5 truncate">{run.template_name}</h3>
        </div>
        <CaretRight size={20} className="text-slate-300 shrink-0 mt-1" />
      </div>
      <div className="mt-4 h-1.5 rounded-full bg-slate-100 overflow-hidden"><div className={`h-full ${bar}`} style={{ width: `${pct}%` }} /></div>
      <div className="flex items-center gap-3 mt-3 flex-wrap text-xs font-semibold text-slate-500">
        <span data-testid={`run-status-${run.id}`} className={`font-bold px-2.5 py-1 rounded-md border ${st.chip}`}>{st.label}</span>
        <span>{run.progress.done}/{run.progress.total} lines</span>
        {run.progress.failed > 0 && <span className="text-red-600 flex items-center gap-1"><WarningCircle size={14} weight="fill" /> {run.progress.failed} exception{run.progress.failed === 1 ? "" : "s"}</span>}
        {run.status === "verified" && <span className="text-emerald-700 flex items-center gap-1"><ShieldCheck size={14} weight="fill" /> {run.verified_by}</span>}
        {run.status === "submitted" && <span>by {run.submitted_by}</span>}
      </div>
    </button>
  );
}
